import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import SearchInput from './SearchInput';
import { galleryItems } from '../pages/OurWork';

type SearchOverlayProps = {
  isOpen: boolean;
  onClose: () => void;
};

const SearchOverlay = ({ isOpen, onClose }: SearchOverlayProps) => {
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  const handleSearch = (query: string) => {
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        {/* Overlay background */}
        <div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Search container */}
        <div className="relative flex items-start justify-center min-h-screen pt-32 px-4 pb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="relative w-full max-w-3xl mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute -top-16 right-0 text-white hover:text-burnt-orange transition-colors"
              aria-label="Close search"
            >
              <X size={32} />
            </button>

            {/* Search input */}
            <div className="relative">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                What are you looking for?
              </h2>
              <SearchInput
                ref={searchInputRef}
                placeholder="Search our work, services, and more..."
                onSearch={handleSearch}
                showSuggestions={true}
                galleryData={galleryItems}
                className="bg-white/10 backdrop-blur-sm border-gray-600 text-white text-lg py-5 px-6"
                autoFocus={true}
              />
              <div className="mt-6 flex justify-center">
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 bg-transparent border border-white/30 text-white rounded-md hover:bg-white/10 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
              <p className="mt-4 text-center text-gray-400 text-sm">
                Type to search our portfolio, services, and content
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SearchOverlay;
